/**
 * AgentMemory - User's AI personalization settings
 * The more they configure, the harder to switch
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Brain, Sparkles, Building2, MessageSquare, Save,
  Loader2, CheckCircle, RotateCcw, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgentMemory() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [insights, setInsights] = useState(null);

  // Form state
  const [brandVoice, setBrandVoice] = useState({
    tone: '',
    style: '',
    vocabulary_level: '',
    avoid_words: [],
    preferred_phrases: []
  });
  const [businessContext, setBusinessContext] = useState({
    company_name: '',
    industry: '',
    target_audience: '',
    company_description: '',
    unique_value_prop: ''
  });
  const [preferences, setPreferences] = useState({
    default_output_format: 'markdown',
    default_length: 'medium',
    creativity_level: 'balanced'
  });
  const [customInstructions, setCustomInstructions] = useState('');

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!user || !token) {
      navigate('/auth?redirect=/agent-memory');
      return;
    }
    fetchMemoryProfile();
    fetchInsights();
  }, [user, token, authLoading]);

  const fetchMemoryProfile = async () => {
    try {
      const response = await axios.get(`${API}/memory/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setProfile(data);
      
      if (data.brand_voice) setBrandVoice({ ...brandVoice, ...data.brand_voice });
      if (data.business_context) setBusinessContext({ ...businessContext, ...data.business_context });
      if (data.preferences) setPreferences({ ...preferences, ...data.preferences });
      if (data.custom_instructions) setCustomInstructions(data.custom_instructions);
    } catch (err) {
      console.error('Failed to fetch memory profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/memory/insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/memory/profile`, {
        brand_voice: brandVoice,
        business_context: businessContext,
        preferences,
        custom_instructions: customInstructions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Memory profile saved! Your AI will now remember these preferences.');
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all learned patterns? Your explicit preferences will be kept.')) return;
    
    try {
      await axios.delete(`${API}/memory/reset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Learning patterns reset');
      fetchInsights();
    } catch (err) {
      toast.error('Failed to reset');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">AI Memory</h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Train your AI to understand your brand, preferences, and business. 
            The more you teach it, the better it gets.
          </p>
        </motion.div>

        {/* Insights Card */}
        {insights && insights.interaction_count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              <h2 className="text-white font-semibold">Learning Progress</h2>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{insights.interaction_count}</p>
                <p className="text-zinc-400 text-sm">Interactions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">{insights.positive_feedback}</p>
                <p className="text-zinc-400 text-sm">Positive</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-400">{insights.negative_feedback}</p>
                <p className="text-zinc-400 text-sm">Needs Improvement</p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="space-y-8">
          {/* Brand Voice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <MessageSquare className="w-5 h-5 text-teal-400" />
              <h2 className="text-xl font-bold text-white">Brand Voice</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Tone</label>
                <select
                  value={brandVoice.tone}
                  onChange={(e) => setBrandVoice({ ...brandVoice, tone: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Select tone...</option>
                  <option value="professional">Professional</option>
                  <option value="casual">Casual & Friendly</option>
                  <option value="authoritative">Authoritative</option>
                  <option value="playful">Playful & Fun</option>
                  <option value="empathetic">Empathetic & Warm</option>
                </select>
              </div>

              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Writing Style</label>
                <select
                  value={brandVoice.style}
                  onChange={(e) => setBrandVoice({ ...brandVoice, style: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Select style...</option>
                  <option value="concise">Concise & Direct</option>
                  <option value="detailed">Detailed & Thorough</option>
                  <option value="storytelling">Storytelling</option>
                  <option value="technical">Technical</option>
                </select>
              </div>

              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Vocabulary Level</label>
                <select
                  value={brandVoice.vocabulary_level}
                  onChange={(e) => setBrandVoice({ ...brandVoice, vocabulary_level: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Select level...</option>
                  <option value="simple">Simple (Grade 8)</option>
                  <option value="mixed">Mixed (General Audience)</option>
                  <option value="technical">Technical (Industry Expert)</option>
                </select>
              </div>

              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Words to Avoid (comma-separated)</label>
                <Input
                  value={brandVoice.avoid_words?.join(', ') || ''}
                  onChange={(e) => setBrandVoice({ 
                    ...brandVoice, 
                    avoid_words: e.target.value.split(',').map(w => w.trim()).filter(Boolean)
                  })}
                  placeholder="e.g., synergy, leverage, disrupt"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
          </motion.div>

          {/* Business Context */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-5 h-5 text-blue-400" />
              <h2 className="text-xl font-bold text-white">Business Context</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Company Name</label>
                <Input
                  value={businessContext.company_name}
                  onChange={(e) => setBusinessContext({ ...businessContext, company_name: e.target.value })}
                  placeholder="Your company name"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Industry</label>
                <Input
                  value={businessContext.industry}
                  onChange={(e) => setBusinessContext({ ...businessContext, industry: e.target.value })}
                  placeholder="e.g., SaaS, E-commerce, Healthcare"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-zinc-400 text-sm mb-2 block">Target Audience</label>
                <Input
                  value={businessContext.target_audience}
                  onChange={(e) => setBusinessContext({ ...businessContext, target_audience: e.target.value })}
                  placeholder="e.g., Small business owners, Tech-savvy millennials"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-zinc-400 text-sm mb-2 block">Company Description</label>
                <Textarea
                  value={businessContext.company_description}
                  onChange={(e) => setBusinessContext({ ...businessContext, company_description: e.target.value })}
                  placeholder="Brief description of what your company does..."
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-zinc-400 text-sm mb-2 block">Unique Value Proposition</label>
                <Input
                  value={businessContext.unique_value_prop}
                  onChange={(e) => setBusinessContext({ ...businessContext, unique_value_prop: e.target.value })}
                  placeholder="What makes you different from competitors?"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
          </motion.div>

          {/* Custom Instructions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <h2 className="text-xl font-bold text-white">Custom Instructions</h2>
            </div>

            <Textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Any special instructions for your AI? e.g., 'Always include a call-to-action', 'Never use emojis', 'Reference our flagship product when relevant'"
              className="bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
            />
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-between"
          >
            <Button
              onClick={handleReset}
              variant="ghost"
              className="text-zinc-400 hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset Learning
            </Button>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Memory Profile
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
